import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useClasses } from '@/features/classes/hooks';
import { DeactivateLearningSpaceButton } from '@/features/learning-spaces/deactivate-learning-space-button';
import { EditLearningSpaceDialog } from '@/features/learning-spaces/edit-learning-space-dialog';
import { useLearningSpace } from '@/features/learning-spaces/hooks';
import { useLearningSpaceProgress } from '@/features/memorization/hooks';
import { ProgressStatsGrid } from '@/features/memorization/progress-stats-grid';
import { useHasCapability } from '@/lib/capabilities';

const CLASS_PREVIEW_LIMIT = 20;

/**
 * The learning space workspace (Phase G-6): profile, its classes, and
 * aggregate Progress. Classes are listed via the same `GET /classes`
 * filter the standalone Classes page uses (`learningSpaceId`) - no
 * duplicate class list is built, only linked with the filter applied.
 */
export function LearningSpaceDetailPage(): JSX.Element {
  const { learningSpaceId } = useParams<{ learningSpaceId: string }>();
  const { data: learningSpace, isLoading, isError } = useLearningSpace(learningSpaceId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const canManage = useHasCapability('learning_spaces:manage');
  const canViewProgress = useHasCapability('memorization_targets:manage');
  const { data: classes } = useClasses(1, CLASS_PREVIEW_LIMIT, learningSpaceId);

  if (learningSpaceId === undefined) {
    return <p className="p-4 text-sm text-destructive">Ruang belajar tidak ditemukan.</p>;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <h1 className="sr-only">Detail Ruang Belajar</h1>
      {isLoading && <Skeleton className="h-32 w-full" />}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat data ruang belajar.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && learningSpace !== undefined && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="min-w-0 truncate text-base">{learningSpace.name}</CardTitle>
              {canManage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditOpen(true);
                  }}
                >
                  Ubah Data
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Badge variant="secondary" className="w-fit capitalize">
                {learningSpace.status.toLowerCase()}
              </Badge>
              {learningSpace.description !== null && (
                <p className="text-sm text-muted-foreground">{learningSpace.description}</p>
              )}
              {canManage && (
                <div className="mt-2">
                  <DeactivateLearningSpaceButton learningSpaceId={learningSpace.id} />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Kelas</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {classes !== undefined && classes.items.length === 0 && (
                <p className="text-sm text-muted-foreground">Belum ada kelas di ruang ini.</p>
              )}
              {classes !== undefined && classes.items.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {classes.items.map((classView) => (
                    <li key={classView.id}>
                      <Link to={`/classes/${classView.id}`}>
                        <Card className="transition-colors hover:bg-secondary/50">
                          <CardContent className="flex items-center justify-between gap-4 p-3">
                            <span className="truncate text-sm font-medium">{classView.name}</span>
                            <Badge variant="secondary" className="capitalize">
                              {classView.status.toLowerCase()}
                            </Badge>
                          </CardContent>
                        </Card>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to={`/classes?learningSpaceId=${learningSpace.id}`}
                className="text-sm text-primary hover:underline"
              >
                Lihat semua kelas
              </Link>
            </CardContent>
          </Card>

          {canViewProgress && <LearningSpaceProgressCard learningSpaceId={learningSpace.id} />}

          <EditLearningSpaceDialog
            learningSpace={learningSpace}
            open={editOpen}
            onOpenChange={setEditOpen}
          />
        </>
      )}
    </div>
  );
}

function LearningSpaceProgressCard({
  learningSpaceId,
}: {
  readonly learningSpaceId: string;
}): JSX.Element {
  const { data, isLoading, isError } = useLearningSpaceProgress(learningSpaceId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progress Ruang Belajar</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat progres ruang belajar.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && (
          <>
            <ProgressStatsGrid data={data} />
            <span className="text-xs text-muted-foreground">
              Terakhir direkonsiliasi:{' '}
              {data.lastReconciledAt !== null
                ? new Date(data.lastReconciledAt).toLocaleString('id-ID')
                : 'Belum pernah'}
            </span>
          </>
        )}
      </CardContent>
    </Card>
  );
}
